import { SHELL_RULES } from "../../config/gameplay";
import { clamp, normalizeDegrees, shortestAngleDistanceDegrees } from "../../utils/math";
import type { UpgradeEffects } from "../upgrades/effects";
import type { CoreColor, ShellPolarityWindow, ShellState } from "../runtime/types";

const polarityColors: readonly CoreColor[] = ["cyan", "pink", "lime", "gold"];

export function buildShells(effects: UpgradeEffects): readonly ShellState[] {
  const shellCount = clamp(SHELL_RULES.startingShells + effects.extraShellCount, 1, SHELL_RULES.maxShells);
  return Array.from({ length: shellCount }, (_, index) => {
    const radius = SHELL_RULES.baseRadius + SHELL_RULES.radiusStep * index;
    const direction = index % 2 === 0 ? 1 : -0.72;
    const baseGate = SHELL_RULES.baseGateDegrees + effects.gateBonusDegrees;
    const gateDegrees = clamp(baseGate * (SHELL_RULES.baseRadius / Math.max(SHELL_RULES.baseRadius, radius)), 8, baseGate);
    return {
      index,
      radius,
      thickness: SHELL_RULES.thickness,
      angleDegrees: -104 + index * 45,
      speedDegrees: SHELL_RULES.baseSpeedDegrees * direction * (1 + index * 0.08) * effects.shellSpeedMultiplier,
      gateDegrees,
      gateCount: effects.gateCount,
      polarityWindows: buildPolarityWindows(index, effects)
    };
  });
}

export function advanceShells(shells: readonly ShellState[], deltaSeconds: number, boosted: boolean): void {
  const boost = boosted ? SHELL_RULES.manualBoostMultiplier : 1;
  for (const shell of shells) {
    shell.angleDegrees = normalizeDegrees(shell.angleDegrees + shell.speedDegrees * boost * deltaSeconds);
  }
}

export function isAngleInGate(shell: ShellState, angleDegrees: number): boolean {
  const gateStep = 360 / shell.gateCount;
  for (let gateIndex = 0; gateIndex < shell.gateCount; gateIndex += 1) {
    const gateCenter = normalizeDegrees(shell.angleDegrees + gateIndex * gateStep);
    if (shortestAngleDistanceDegrees(angleDegrees, gateCenter) <= shell.gateDegrees / 2) {
      return true;
    }
  }
  return false;
}

export function polarityWindowCenterDegrees(shell: ShellState, window: ShellPolarityWindow): number {
  return normalizeDegrees(shell.angleDegrees + window.offsetDegrees);
}

export function isAngleInPolarityWindow(shell: ShellState, angleDegrees: number, color: CoreColor): boolean {
  for (const window of shell.polarityWindows) {
    if (window.color !== color) {
      continue;
    }

    const center = polarityWindowCenterDegrees(shell, window);
    if (shortestAngleDistanceDegrees(angleDegrees, center) <= window.halfDegrees) {
      return true;
    }
  }
  return false;
}

export function outerShell(shells: readonly ShellState[]): ShellState {
  return shells[shells.length - 1];
}

function buildPolarityWindows(shellIndex: number, effects: UpgradeEffects): readonly ShellPolarityWindow[] {
  if (!effects.polarityEnabled || effects.polarityWindowCount <= 0) {
    return [];
  }

  const stepDegrees = 360 / effects.polarityWindowCount;
  const startOffset = stepDegrees / 2 + shellIndex * 23;
  return Array.from({ length: effects.polarityWindowCount }, (_, index) => ({
    offsetDegrees: normalizeDegrees(startOffset + index * stepDegrees),
    halfDegrees: effects.polarityWindowHalfDegrees,
    color: polarityColors[(index + shellIndex) % polarityColors.length]
  }));
}
